/** Notes tab — list + composer. */
import { useState } from 'react';
import { useAddNote, type LeadNote } from '@/features/pipeline/api';
import { errorMessage, useToast } from '@/components/Toast';
import { fullDateTime, relativeTime } from '@/lib/time';

export function LeadNotesTab({ leadId, notes }: { leadId: string; notes: LeadNote[] }) {
  const [body, setBody] = useState('');
  const addNote = useAddNote(leadId);
  const toast = useToast();
  const sorted = [...notes].sort((a, b) => (a.created_at < b.created_at ? 1 : -1));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const text = body.trim();
    if (!text) return;
    addNote.mutate(text, {
      onSuccess: () => setBody(''),
      onError: (err) => toast.push({ kind: 'error', title: 'Could not add note', message: errorMessage(err) }),
    });
  };

  return (
    <div className="space-y-4">
      <form onSubmit={submit}>
        <label htmlFor="note-body" className="sr-only">
          New note
        </label>
        <textarea
          id="note-body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') submit(e);
          }}
          rows={3}
          placeholder="Add a note — what did you learn about this candidate?"
          className="block w-full resize-y rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-500"
        />
        <div className="mt-2 flex items-center justify-between gap-2">
          <span className="text-[12px] text-muted">Ctrl+Enter to save</span>
          <button
            type="submit"
            disabled={!body.trim() || addNote.isPending}
            className="inline-flex min-h-10 items-center rounded-md bg-navy px-3.5 text-sm font-medium text-white hover:bg-navy-dark disabled:opacity-50"
          >
            {addNote.isPending ? 'Saving…' : 'Add note'}
          </button>
        </div>
      </form>

      {sorted.length === 0 ? (
        <p className="py-4 text-center text-sm text-muted">No notes yet.</p>
      ) : (
        <ul className="space-y-2.5">
          {sorted.map((n) => (
            <li key={n.id} className="rounded-lg border border-gray-200 bg-surface px-3 py-2.5">
              <p className="whitespace-pre-wrap text-[13px] leading-5 text-gray-900">{n.body}</p>
              <div className="mt-1.5 flex flex-wrap items-center gap-x-1.5 text-[12px] text-muted">
                <span>{n.author_name ?? (n.author_kind === 'user' ? 'Team member' : n.author_kind === 'sms' ? 'Text reply' : n.author_kind)}</span>
                {n.author_kind === 'sms' && (
                  <span className="rounded bg-page px-1.5 py-0.5 font-medium text-gray-700" data-testid="note-via-text">via text</span>
                )}
                <span aria-hidden> · </span>
                <time dateTime={n.created_at} title={fullDateTime(n.created_at)}>
                  {relativeTime(n.created_at)}
                </time>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
